import { API_URL } from "../components/common/Config";

const GLOBAL_CACHE_KEY = "__ebussoCompanySettingsCache";

let fallbackStore = null;

function getSessionStorageKey() {
  const prefix = localStorage.getItem("accessToken") || "default";
  return `ebusso_company_settings_${prefix}`;
}

function readSettingsFromSession() {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    const raw = sessionStorage.getItem(getSessionStorageKey());
    if (!raw) {
      return undefined;
    }
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function writeSettingsToSession(settings) {
  if (typeof window === "undefined" || !settings) {
    return;
  }
  try {
    sessionStorage.setItem(getSessionStorageKey(), JSON.stringify(settings));
  } catch {
    // ignore quota / private mode errors
  }
}

function removeSettingsFromSession() {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(getSessionStorageKey());
  } catch {
    // ignore
  }
}

function hydrateStoreFromSession(store) {
  if (store.cachedSettings !== undefined) {
    return;
  }
  const fromSession = readSettingsFromSession();
  if (fromSession !== undefined) {
    store.cachedSettings = fromSession;
  }
}

function getStore() {
  if (typeof window !== "undefined") {
    if (!window[GLOBAL_CACHE_KEY]) {
      window[GLOBAL_CACHE_KEY] = {
        cachedSettings: undefined,
        inflightPromise: null,
      };
    }
    const store = window[GLOBAL_CACHE_KEY];
    hydrateStoreFromSession(store);
    return store;
  }
  if (!fallbackStore) {
    fallbackStore = {
      cachedSettings: undefined,
      inflightPromise: null,
    };
  }
  return fallbackStore;
}

export function clearCompanySettingsCache() {
  const store = getStore();
  store.cachedSettings = undefined;
  store.inflightPromise = null;
  removeSettingsFromSession();
}

export function isCompanySettingsLoaded() {
  return getStore().cachedSettings !== undefined;
}

/** Sync read — null when not loaded yet or when API returned no data. */
export function getCachedCompanySettings() {
  const { cachedSettings } = getStore();
  return cachedSettings !== undefined ? cachedSettings : null;
}

/** Warm cache once; safe to call from many places (deduped globally). */
export function prefetchCompanySettings() {
  return getCompanySettings();
}

export function resolveCustomerApiEncrypt(settings) {
  if (settings === null || settings === undefined) {
    return true;
  }
  return String(settings.is_encrypt ?? "").toLowerCase() === "yes";
}

export async function getCompanySettings() {
  const store = getStore();

  if (store.cachedSettings !== undefined) {
    return store.cachedSettings;
  }

  if (!store.inflightPromise) {
    store.inflightPromise = (async () => {
      try {
        const authToken = localStorage.getItem("token");
        if (!authToken) {
          return null;
        }
        const response = await fetch(`${API_URL}/settings/ws-get-company-settings`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const result = await response.json();
        store.cachedSettings = result?.data ?? null;
        if (store.cachedSettings) {
          writeSettingsToSession(store.cachedSettings);
        }
        if (store.cachedSettings?.QuickBooksConnectivity != null) {
          localStorage.setItem(
            "QuickBooksConnectivity",
            String(store.cachedSettings.QuickBooksConnectivity)
          );
        }
        return store.cachedSettings;
      } catch {
        store.cachedSettings = null;
        return null;
      } finally {
        store.inflightPromise = null;
      }
    })();
  }

  return store.inflightPromise;
}

export async function ensureCompanySettings() {
  if (isCompanySettingsLoaded()) {
    return getCachedCompanySettings();
  }
  return getCompanySettings();
}

export function resolveIsPriceEditable(settings) {
  if (settings === null || settings === undefined) {
    return true;
  }
  return settings.is_price_editable === "Y";
}

export function resolveCheckStockBeforeAddToCart(settings) {
  if (settings === null || settings === undefined) {
    return true;
  }
  return settings.checkStockBeforeAddToCart !== "N";
}

/** When false (displayProductStock N / 0), stock limits should not be enforced in sales UI. */
export function resolveDisplayProductStock(settings) {
  if (settings === null || settings === undefined) {
    return true;
  }
  const value = String(settings.displayProductStock ?? "").trim().toUpperCase();
  if (value === "N" || value === "0" || value === "NO" || value === "FALSE") {
    return false;
  }
  return true;
}
